import Big from 'big.js'
import { UsePriceFeedsData } from 'src/common/queries/usePriceFeeds/usePriceFeeds'
import { getPriceFromPriceFeed } from 'src/common/queries/usePriceFeeds/utils'
import { LockedAmount } from 'src/common/types/models/LockedAmount'
import { Offer, OfferAsset } from 'src/common/types/models/Offer'
import {
  ERC20,
  ERC20Asset,
  NFT,
  NFTAsset,
  TokenType,
} from 'src/common/types/models/Tokens'
import { big, normalize, ZERO } from 'src/common/utils/big-utils'
import { calcDynamicOfferAmountOut } from 'src/common/utils/offers/offerPrices'
import { areAddressesEqual } from 'src/common/utils/web3/areAddressesEqual'
import { isERC20Type, isNFTType } from 'src/common/utils/web3/tokens'
import { unifyAddress } from 'src/common/utils/web3/unifyAddress'
import staticConfig from 'src/config'
import { polygonEnvironment } from 'src/config/networks/polygon'
import { ARBITRUM_SEPOLIA_KNOWN_TOKENS } from 'src/config/tokens/arbitrum-sepolia-known-tokens'
import {
  LockedAmountsInOtcQuery as LockedAmountsInOtcQueryGQL,
  OfferPricingType,
  OffersQuery as OffersQueryGQL,
  PercentageType,
  TokensQuery as TokensQueryGQL,
  TokenStandard,
} from '../types/swarmx/graphql'
import { AssetHelper } from 'src/common/utils/offers/assetHelper'
import { DotcEnums } from 'src/common/contracts/enums/dotc'

const { isDevMode } = staticConfig

const normalizeAssetType = (assetType: TokenStandard): TokenType => {
  if (assetType === TokenStandard.Erc20) return 'ERC20'
  if (assetType === TokenStandard.Erc721) return 'ERC721'
  if (assetType === TokenStandard.Erc1155) return 'ERC1155'
  throw Error('Unknown token type')
}

const normalizeERC20 = (
  token: TokensQueryGQL['assets'][0]
): ERC20Asset | null => {
  if (!isERC20Type(token)) return null

  const tradedVolume = token.tradedVolume
  const kya = token.kya ?? undefined

  if (
    isDevMode &&
    areAddressesEqual(
      ARBITRUM_SEPOLIA_KNOWN_TOKENS.EUROS.contractAddress,
      token.address
    )
  ) {
    return {
      ...ARBITRUM_SEPOLIA_KNOWN_TOKENS.EUROS,
      tradedVolume,
      kya,
    }
  }

  const erc20 = {
    id: token.id,
    contractAddress: unifyAddress(token.address),
    name: token.name,
    symbol: token.symbol,
    decimals: token.decimals,
    logo: '',
    type: normalizeAssetType(token.tokenStandard) as 'ERC20',
    tradedVolume,
    kya,
    assetType: token.assetType,
  }

  if (areAddressesEqual(polygonEnvironment.tokens.usdce, token.address)) {
    erc20.symbol = 'USDC.e'
    erc20.name = 'Bridged USDC'
  }

  return erc20
}

const normalizeNFT = (token: TokensQueryGQL['assets'][0]): NFTAsset | null => {
  const tradedVolume = token.tradedVolume
  const kya = token.kya ?? undefined

  if (isNFTType(token)) {
    return {
      id: token.id,
      contractAddress: unifyAddress(token.address),
      name: token.name,
      symbol: token.symbol,
      tokenId: token.tokenId,
      logo: '',
      type: token.tokenStandard as 'ERC721' | 'ERC1155',
      tradedVolume,
      kya,
      assetType: token.assetType,
    }
  }
  return null
}

export const normalizeToken = (
  token: TokensQueryGQL['assets'][0]
): ERC20Asset | NFTAsset => {
  const normalizedERC20 = normalizeERC20(token)
  if (normalizedERC20) {
    return normalizedERC20
  }

  const normalizedNFT = normalizeNFT(token)
  return normalizedNFT as NFTAsset
}

export const normalizeAllERC20s = (
  tokens: TokensQueryGQL['assets']
): ERC20Asset[] => {
  return tokens.reduce<ERC20Asset[]>((acc, token) => {
    const normalizedERC20 = normalizeERC20(token)
    if (normalizedERC20) {
      acc.push(normalizedERC20)
    }
    return acc
  }, [])
}

export const normalizeAllNFTs = (
  tokens: TokensQueryGQL['assets']
): NFTAsset[] => {
  return tokens.reduce<NFTAsset[]>((acc, token) => {
    const normalizedNFT = normalizeNFT(token)
    if (normalizedNFT) {
      acc.push(normalizedNFT)
    }
    return acc
  }, [])
}

const getOfferAmounts = (
  depositAsset: ERC20 | NFT,
  withdrawalAsset: ERC20 | NFT,
  offer: OffersQueryGQL['offers'][0],
  priceFeeds: UsePriceFeedsData
) => {
  const amountIn = isNFTType(depositAsset)
    ? Big(offer.amountIn)
    : normalize(offer.amountIn, depositAsset.decimals)

  const availableAmount = isNFTType(depositAsset)
    ? Big(offer.availableAmount)
    : normalize(offer.availableAmount, depositAsset.decimals)

  const percentage = offer.offerPrice.percentage
    ? normalize(offer.offerPrice.percentage, 2)
    : null
  const _percentageType = offer.offerPrice.percentageType || null
  const percentageType =
    _percentageType &&
    (_percentageType === PercentageType.Plus
      ? DotcEnums.PercentageType.Plus
      : DotcEnums.PercentageType.Minus)

  let pricePerUnitSell
  let pricePerUnitBuy

  if (offer.offerPrice.pricingType === OfferPricingType.DynamicPricing) {
    pricePerUnitSell = getPriceFromPriceFeed(
      priceFeeds,
      depositAsset.contractAddress
    )
    pricePerUnitBuy = getPriceFromPriceFeed(
      priceFeeds,
      withdrawalAsset.contractAddress
    )
  }

  if (pricePerUnitSell && pricePerUnitBuy) {
    const { withdrawalAmountPaid, depositToWithdrawalRate } =
      AssetHelper.calcRateAndPrice(
        depositAsset,
        withdrawalAsset,
        offer,
        pricePerUnitSell,
        pricePerUnitBuy
      )

    const amountOut = offer.amountOut
      ? isNFTType(withdrawalAsset) // TODO: Adjust NFTs handling
        ? Big(offer.amountOut)
        : calcDynamicOfferAmountOut(
            amountIn,
            percentage,
            percentageType,
            pricePerUnitSell,
            pricePerUnitBuy
          )
      : null

    return {
      amountIn,
      amountOut,
      availableAmount,
      withdrawalAmountPaid,
      depositToWithdrawalRate,
    }
  } else {
    const amountOut = offer.amountOut
      ? isNFTType(withdrawalAsset)
        ? Big(offer.amountOut)
        : normalize(offer.amountOut, withdrawalAsset.decimals)
      : null

    return {
      amountIn,
      amountOut,
      availableAmount,
    }
  }
}

export const normalizeOfferAssets = (
  offer: OffersQueryGQL['offers'][0]
): {
  depositAsset: OfferAsset
  withdrawalAsset: OfferAsset
} => {
  const depositAsset: OfferAsset = {
    ...normalizeToken(offer.depositAsset),
    priceFeedAddress: offer.offerPrice.depositAssetPrice?.priceFeedAddress,
  }
  const withdrawalAsset: OfferAsset = {
    ...normalizeToken(offer.withdrawalAsset),
    priceFeedAddress: offer.offerPrice.withdrawalAssetPrice?.priceFeedAddress,
  }

  return { depositAsset, withdrawalAsset }
}

export const normalizeOffer = (
  offer: OffersQueryGQL['offers'][0],
  priceFeeds: UsePriceFeedsData
): Offer => {
  const { depositAsset, withdrawalAsset } = normalizeOfferAssets(offer)

  const price = offer.offerPrice
  const percentage = price.percentage ? normalize(price.percentage, 2) : null

  const unitPrice = offer.offerPrice.unitPrice
    ? big(offer.offerPrice.unitPrice)
    : null

  const {
    amountIn,
    amountOut,
    availableAmount,
    depositToWithdrawalRate,
    withdrawalAmountPaid,
  } = getOfferAmounts(depositAsset, withdrawalAsset, offer, priceFeeds)

  const isCompleted = availableAmount.eq(ZERO)

  return {
    id: offer.id,
    amountIn, // Use it ONLY to show initial amountIn
    amountOut,
    availableAmount,
    depositToWithdrawalRate,
    withdrawalAmountPaid,
    maker: offer.maker,
    offerStatus: offer.offerStatus,
    offerType: offer.offerType,
    specialAddresses: offer.specialAddresses || null,
    authorizationAddresses: offer.authorizationAddresses || null,
    isAuth: offer.isAuth,
    isPrivate: offer.isPrivate,
    isCompleted,
    timelockPeriod: Number(offer.timelockPeriod),
    expiryTimestamp: Number(offer.expiryTimestamp),
    cancelledAt: Number(offer.cancelledAt) || null,
    createdAt: Number(offer.createdAt),
    depositAsset,
    withdrawalAsset,
    terms: offer.terms || null,
    commsLink: offer.commsLink || null,
    offerPrice: {
      id: price.id,
      percentage, // 10000 - 100% 10000 - decimals 2
      percentageType: price.percentageType || null,
      pricingType: price.pricingType,
      unitPrice: unitPrice || null,
    },
  }
}

export const normalizeAllOffers = (
  offers: OffersQueryGQL['offers'],
  priceFeeds: UsePriceFeedsData
): Offer[] => {
  return offers
    .map(offer => normalizeOffer(offer, priceFeeds))
}

export const normalizeLockedAmount = (
  offer: LockedAmountsInOtcQueryGQL['offers'][0]
): LockedAmount => {
  const normalizedDepositAsset = normalizeToken(offer.depositAsset)

  const lockedAmount = isERC20Type(normalizedDepositAsset)
    ? normalize(offer.availableAmount, normalizedDepositAsset.decimals)
    : Big(offer.availableAmount)

  return {
    id: offer.id,
    lockedAmount, // Use it ONLY to show initial amountIn
    lockedToken: normalizedDepositAsset,
    maker: offer.maker,
    offerStatus: offer.offerStatus,
  }
}

export const normalizeAllLockedAmounts = (
  offers: LockedAmountsInOtcQueryGQL['offers']
): LockedAmount[] => {
  return offers.map(offer => normalizeLockedAmount(offer))
}
